import { useGetCommunity } from '@sunrise-ui/api-client';
import {
  ProfileBasicInfo,
  ProfileImg,
  TextBold,
  TextReg,
  useDynamicStyles,
  useMediaQueries,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import React from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  View,
  type ViewStyle,
} from 'react-native';

export type CommunityResultProps = {
  communityId?: string;
  size?: 'small' | 'medium';
  style?: ViewStyle;
  shouldNavigateOnClick?: boolean;
  isOneLineText?: boolean;
  onPress?: () => void;
  centerText?: boolean;
};

export function CommunityResult({
  communityId,
  size = 'medium',
  style,
  shouldNavigateOnClick,
  isOneLineText,
  onPress,
  centerText = false,
}: CommunityResultProps) {
  const { isMobile } = useMediaQueries();
  const styles = useDynamicStyles(createStyles);

  const { data: community, isLoading } = useGetCommunity(communityId);

  const handleCommunityPress = () => {
    const slug = community?.slug;
    if (slug) {
      onPress?.();
      router.navigate(`/communities/${slug}`);
    }
  };

  const content = centerText ? (
    <>
      <ProfileImg
        userId={community?.id}
        imgSize="large"
        styleSize={isMobile || size === 'small' ? 50 : 95}
        profile={{
          first_name: community?.name,
          last_name: '',
          photos: community?.photos,
        }}
        isLoading={isLoading}
      />
      <View style={styles.centeredTextContainer}>
        <TextBold
          style={styles.centeredName}
          numberOfLines={isOneLineText ? 1 : 2}
        >
          {community?.name}
        </TextBold>
        {community?.headline && (
          <TextReg
            style={styles.centeredHeadline}
            numberOfLines={isOneLineText ? 1 : 2}
          >
            {community.headline}
          </TextReg>
        )}
      </View>
    </>
  ) : (
    <>
      <ProfileImg
        userId={community?.id}
        imgSize="large"
        styleSize={isMobile || size === 'small' ? 50 : 95}
        profile={{
          first_name: community?.name,
          last_name: '',
          photos: community?.photos,
        }}
        isLoading={isLoading}
      />
      <ProfileBasicInfo
        firstName={community?.name}
        headline={community?.headline}
        isLoading={isLoading}
        size={size}
        isOneLineText={isOneLineText}
      />
    </>
  );

  if (shouldNavigateOnClick) {
    return (
      <TouchableOpacity
        style={[styles.container, style]}
        onPress={handleCommunityPress}
      >
        {content}
      </TouchableOpacity>
    );
  }

  return <View style={[styles.container, style]}>{content}</View>;
}

const createStyles = () =>
  StyleSheet.create({
    container: {
      display: 'flex',
      flexDirection: 'row',
      gap: 8,
      alignItems: 'center',
      flexGrow: 1,
      flexShrink: 1,
    },
    centeredTextContainer: {
      flexGrow: 1,
      flexShrink: 1,
      alignItems: 'center',
      gap: 4,
    },
    centeredName: {
      fontSize: 16,
      textAlign: 'center',
    },
    centeredHeadline: {
      fontSize: 12,
      textAlign: 'center',
      opacity: 0.8,
    },
  });
